// src/lib/loyalty.ts
// Helper murni untuk fitur "Program Poin" (Loyalty Points, mig 139/140).
// Cermin TS dari RUMUS KELIPATAN award + penukaran, untuk tampilan admin
// (editor program) & storefront (kartu poin / penukaran).
//
// ⚠️ Batas cerminnya (bughunt F3-7): yang dicerminkan HANYA aritmetika
// `floor(total / threshold) × pointsValue`. Yang menentukan `total` — join
// produk, filter target/kategori/merek, `product_type_scope`, `cancelled_at`,
// dan idempotensi net per (pelanggan × program × nota) — hidup HANYA di SQL
// (`award_loyalty_points_for_invoice`, mig 3080/5160) dan tidak punya cermin
// di sini. Jangan pakai berkas ini sebagai sumber kebenaran jumlah poin nota.
// TANPA dependensi Supabase → mudah diuji.

import { formatShortDate } from "@/lib/reports/format";
import { wibDateBoundaryIso } from "@/lib/wib";

export type ProductTypeScope = "preorder" | "ready_stock" | "all";
export type ProgramBasis = "pcs" | "nominal";
export type TargetType = "product" | "category" | "brand" | "all";
export type TargetKind = "product" | "category" | "brand";

export type LoyaltyTarget = {
  kind: TargetKind;
  productId: string | null;
  name: string;
};

export type LoyaltyProgram = {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  startsAt: string | null; // ISO
  endsAt: string | null; // ISO
  productTypeScope: ProductTypeScope;
  basis: ProgramBasis;
  targetType: TargetType;
  threshold: number; // kelipatan / minimal (pcs atau Rp)
  pointsValue: number; // Nilai Poin per kelipatan
  targets: LoyaltyTarget[];
  createdAt: string | null;
};

export type LoyaltyProgramDraft = {
  id: string | null;
  name: string;
  description: string;
  isActive: boolean;
  startsAt: string | null; // yyyy-mm-dd atau ISO
  endsAt: string | null;
  productTypeScope: ProductTypeScope;
  basis: ProgramBasis;
  targetType: TargetType;
  threshold: number;
  pointsValue: number;
  targets: LoyaltyTarget[];
  /**
   * "Terapkan juga ke nota yang sudah lunas" (bughunt F7-2, mig 6810): nota
   * lunas dalam periode program + nota yang sudah diberi poin program ini
   * dihitung ulang dengan aturan draft, HANYA-NAIK. Wajib bila aturan program
   * yang sudah memberi poin diubah — tanpa itu server menolak (aturan dikunci).
   */
  applyRetro: boolean;
};

/** Keluaran RPC `admin_upsert_loyalty_program` (mig 6810). */
export type LoyaltyProgramSaveResult = {
  /** null pada pratinjau (`dryRun`) — tulisan server sudah dibatalkan. */
  id: string | null;
  /** Aturan poin (nilai/kelipatan/dasar/status produk/target) berubah. */
  rulesChanged: boolean;
  dryRun: boolean;
  /** Ringkasan terapkan mundur; null bila tak diminta. */
  retro: { invoices: number; customers: number; points: number } | null;
};

export type LoyaltyReward = {
  id: string;
  name: string;
  description: string | null;
  pointsCost: number;
  stock: number | null; // null = tak terbatas
  imageUrl: string | null;
  isActive: boolean;
  createdAt: string | null;
};

export type LoyaltyRewardDraft = {
  id: string | null;
  name: string;
  description: string;
  pointsCost: number;
  stock: number | null;
  /**
   * F4-2: stok yang TERBACA saat formulir dibuka — bukan bagian dari payload.
   * Dipakai dua kali: menentukan apakah admin benar-benar mengubah stok
   * (`rewardStockChanged`), dan sebagai penjaga bentrok saat menuliskannya.
   */
  originalStock: number | null;
  imageUrl: string | null;
  isActive: boolean;
};

/**
 * Cermin CHECK `loyalty_transactions_type_check` (mig 1392 → 1430). Menambah
 * jenis di DB tanpa di sini membuat barisnya jatuh ke fallback `earn` — dulu
 * 'expire' (mig 1430) terbaca "Poin dari pembelian". Dikunci
 * `loyaltyTxnTypeLint.test.ts`, yang membaca CHECK terakhir dari migrasi.
 */
export type LoyaltyTxnType =
  | "earn"
  | "redeem_saldo"
  | "redeem_reward"
  | "admin_adjust"
  | "revoke"
  | "refund"
  | "expire";

export type LoyaltyTxn = {
  id: string;
  type: LoyaltyTxnType;
  direction: "in" | "out";
  points: number;
  balanceAfter: number;
  programId: string | null;
  referenceId: string | null;
  note: string | null;
  createdAt: string | null;
};

export type RedemptionStatus = "fulfilled" | "pending" | "rejected";

export type LoyaltyRedemption = {
  id: string;
  customerId: string;
  type: "saldo" | "reward";
  rewardId: string | null;
  rewardName: string | null;
  pointsSpent: number;
  rupiahCredited: number | null;
  status: RedemptionStatus;
  note: string | null;
  createdAt: string | null;
  fulfilledAt: string | null;
};

export type LoyaltyPointsBalance = {
  points: number;
  lifetimePoints: number;
};

export type LoyaltySettings = {
  saldoEnabled: boolean;
  /**
   * F4-4: `null` = toko BELUM mengatur kurs (baris setelan hilang, kosong, bukan
   * angka, atau <= 0). Dulu kasus itu jatuh ke default Rp 100 dan storefront
   * menjanjikan angka rupiah yang tak akan pernah dibayar — di Galea kursnya
   * Rp 1, jadi defaultnya 100× kurs sebenarnya. RPC `redeem_points_to_saldo`
   * tidak punya default: kurs <= 0 → `raise 'Kurs penukaran belum diatur'`.
   */
  rupiahPerPoint: number | null;
  minRedeemPoints: number;
  /** Kedaluwarsa poin (mig 143): poin "earn" hangus setelah N hari. */
  expiryEnabled: boolean;
  expiryDays: number;
};

export const DEFAULT_LOYALTY_SETTINGS: LoyaltySettings = {
  saldoEnabled: false,
  rupiahPerPoint: null, // F4-4: belum diatur ≠ Rp 100
  minRedeemPoints: 0,
  expiryEnabled: false,
  expiryDays: 180,
};

/** Nilai awal yang DISARANKAN di formulir admin — bukan nilai tampilan storefront. */
export const LOYALTY_RUPIAH_PER_POINT_SARAN = 100;

export const PRODUCT_TYPE_SCOPE_OPTIONS: { value: ProductTypeScope; label: string }[] = [
  { value: "all", label: "Semua" },
  { value: "preorder", label: "Pre-Order" },
  { value: "ready_stock", label: "Ready Stock" },
];

export const BASIS_OPTIONS: { value: ProgramBasis; label: string }[] = [
  { value: "pcs", label: "Jumlah pcs" },
  { value: "nominal", label: "Nominal belanja" },
];

export const TARGET_TYPE_OPTIONS: { value: TargetType; label: string }[] = [
  { value: "all", label: "Semua produk" },
  { value: "product", label: "Produk tertentu" },
  { value: "category", label: "Kategori tertentu" },
  { value: "brand", label: "Merek tertentu" },
];

export const REDEMPTION_STATUS_META: Record<
  RedemptionStatus,
  { label: string; tone: "green" | "amber" | "red" }
> = {
  fulfilled: { label: "Selesai", tone: "green" },
  pending: { label: "Menunggu", tone: "amber" },
  rejected: { label: "Ditolak", tone: "red" },
};

export const TXN_TYPE_LABEL: Record<LoyaltyTxnType, string> = {
  earn: "Poin dari pembelian",
  redeem_saldo: "Tukar ke saldo",
  redeem_reward: "Tukar hadiah",
  admin_adjust: "Penyesuaian admin",
  revoke: "Pencabutan poin",
  refund: "Pengembalian poin",
  expire: "Poin kedaluwarsa",
};

// ── Formatter ────────────────────────────────────────────────────────────────

export function formatRupiah(n: number): string {
  return "Rp " + Math.round(Number(n) || 0).toLocaleString("id-ID");
}

export function formatPoints(n: number): string {
  return (Math.round(Number(n) || 0)).toLocaleString("id-ID") + " poin";
}

// ── Mesin kelipatan (sumber rumus tunggal, cermin SQL award) ─────────────────

/**
 * Poin = floor(total / threshold) × pointsValue (kelipatan berulang).
 * Guard: threshold ≤ 0 / total ≤ 0 / pointsValue ≤ 0 → 0.
 *
 * Cocok dengan SQL untuk threshold BULAT — satu-satunya nilai yang masuk akal
 * bagi pcs maupun rupiah, dan sejak bughunt F3-7 satu-satunya yang lolos
 * `validateProgramDraft`. Threshold pecahan divergen: Postgres membagi
 * `numeric` (desimal eksak), JS membagi float64, jadi 0,3 ÷ 0,1 memberi 3 di
 * SQL tapi 2 di sini.
 */
export function computeProgramPoints(
  threshold: number,
  pointsValue: number,
  total: number,
): number {
  const t = Number(threshold) || 0;
  const v = Math.trunc(Number(pointsValue) || 0);
  const tot = Number(total) || 0;
  if (t <= 0 || v <= 0 || tot <= 0) return 0;
  return Math.floor(tot / t) * v;
}

/** Nilai rupiah hasil tukar `points` poin. */
export function pointsToRupiah(points: number, rupiahPerPoint: number | null): number {
  const p = Math.trunc(Number(points) || 0);
  const r = rupiahPerPoint === null ? 0 : Number(rupiahPerPoint) || 0;
  if (p <= 0 || r <= 0) return 0;
  return Math.floor(p * r);
}

// ── Periode program: tanggal lokal → ISO ber-offset WIB ──────────────────────

/**
 * Kelolify beroperasi di WIB (UTC+7); periode program dibaca dalam zona itu.
 *
 * Tanpa offset eksplisit, `admin_upsert_loyalty_program` meng-cast stringnya
 * `::timestamptz` pada sesi PostgREST yang berjalan di UTC, sehingga
 * "1 Apr 23:59:59" tersimpan sebagai 2 Apr 06:59:59 WIB — periode program
 * meleset 7 jam di KEDUA ujung: nota yang lunas 00:00–07:00 WIB pada hari
 * mulai tidak dapat poin, dan nota yang lunas 00:00–06:59 WIB sehari SESUDAH
 * tanggal berakhir masih dapat (bughunt F3-4).
 *
 * Implementasinya di `@/lib/wib` (dipakai bersama editor tanggal admin lain
 * sejak bughunt I2-zona-waktu) — diekspor ulang di sini supaya pemanggil
 * lama (`AdminLoyalty.tsx`, `loyalty.test.ts`) tidak perlu berubah.
 */
export { wibDateBoundaryIso };

// ── Deskripsi & status program ───────────────────────────────────────────────

function thresholdLabel(basis: ProgramBasis, threshold: number): string {
  return basis === "nominal" ? formatRupiah(threshold) : `${Math.round(threshold)} pcs`;
}

function scopeLabel(scope: ProductTypeScope): string {
  return PRODUCT_TYPE_SCOPE_OPTIONS.find((o) => o.value === scope)?.label ?? "Semua";
}

/** Ringkasan target ("Merek: A, B" / "Semua produk"). */
export function describeTargets(program: Pick<LoyaltyProgram, "targetType" | "targets">): string {
  if (program.targetType === "all") return "Semua produk";
  const names = program.targets.map((t) => t.name).filter(Boolean);
  const head =
    program.targetType === "product"
      ? "Produk"
      : program.targetType === "category"
        ? "Kategori"
        : "Merek";
  if (names.length === 0) return `${head} tertentu`;
  return `${head}: ${names.join(", ")}`;
}

/** Kalimat ringkas program (kartu admin). */
export function describeProgram(
  program: Pick<
    LoyaltyProgram,
    "basis" | "threshold" | "pointsValue" | "targetType" | "targets" | "productTypeScope"
  >,
): string {
  return `${productPointsHint(program)} · ${describeTargets(program)} · ${scopeLabel(
    program.productTypeScope,
  )}`;
}

/**
 * Periode program dalam kalender WIB, mis. "1 Sep 2026 — 1 Apr 2027"; null
 * bila tanpa batas di kedua ujung. JANGAN `iso.slice(0, 10)`: PostgREST
 * memulangkan timestamptz dalam UTC, jadi awal hari WIB (00:00 +07) terbaca
 * sehari SEBELUMNYA — kartu Reward Sarimbit 2027 dulu tertulis "2026-08-31".
 */
export function formatProgramPeriod(
  program: Pick<LoyaltyProgram, "startsAt" | "endsAt">,
): string | null {
  if (!program.startsAt && !program.endsAt) return null;
  const start = program.startsAt ? formatShortDate(program.startsAt) : "…";
  const end = program.endsAt ? formatShortDate(program.endsAt) : "tanpa batas";
  return `${start} — ${end}`;
}

export type ProgramStatus = {
  label: string;
  tone: "green" | "amber" | "gray";
};

/** Status tampilan program berdasar periode & toggle aktif. */
export function programStatusLabel(
  program: Pick<LoyaltyProgram, "isActive" | "startsAt" | "endsAt">,
  now: Date = new Date(),
): ProgramStatus {
  if (!program.isActive) return { label: "Nonaktif", tone: "gray" };
  const t = now.getTime();
  if (program.startsAt) {
    const s = new Date(program.startsAt).getTime();
    if (Number.isFinite(s) && t < s) return { label: "Terjadwal", tone: "amber" };
  }
  if (program.endsAt) {
    const e = new Date(program.endsAt).getTime();
    if (Number.isFinite(e) && t > e) return { label: "Berakhir", tone: "gray" };
  }
  return { label: "Berjalan", tone: "green" };
}

/** Apakah program sedang berjalan (aktif + dalam periode). Cermin filter SQL. */
export function isProgramRunning(
  program: Pick<LoyaltyProgram, "isActive" | "startsAt" | "endsAt">,
  now: Date = new Date(),
): boolean {
  return programStatusLabel(program, now).label === "Berjalan";
}

// ── Pencocokan produk → program (surfacing storefront) ───────────────────────

/** Bentuk produk minimal utk pencocokan program (storefront). */
export type ProductForLoyalty = {
  id: string;
  brand?: string | null;
  category?: string | null;
  productType?: "preorder" | "ready_stock" | null;
};

/**
 * Program AKTIF & dalam periode yang berlaku untuk satu produk — cermin filter
 * SQL `award_loyalty_points_for_invoice` (status produk + target produk/kategori/
 * merek, case-insensitive). Dipakai badge "+poin" di halaman produk.
 */
export function programsForProduct(
  product: ProductForLoyalty,
  programs: LoyaltyProgram[],
  now: Date = new Date(),
): LoyaltyProgram[] {
  const brand = (product.brand ?? "").trim().toLowerCase();
  const category = (product.category ?? "").trim().toLowerCase();
  return programs.filter((p) => {
    if (!isProgramRunning(p, now)) return false;
    if (p.productTypeScope !== "all") {
      if (!product.productType || product.productType !== p.productTypeScope) return false;
    }
    switch (p.targetType) {
      case "all":
        return true;
      case "product":
        return p.targets.some((t) => t.kind === "product" && t.productId === product.id);
      case "category":
        return (
          !!category &&
          p.targets.some((t) => t.kind === "category" && t.name.trim().toLowerCase() === category)
        );
      case "brand":
        return (
          !!brand &&
          p.targets.some((t) => t.kind === "brand" && t.name.trim().toLowerCase() === brand)
        );
      default:
        return false;
    }
  });
}

/** Kalimat singkat potensi poin satu program ("Tiap 1 pcs → 1.500 poin"). */
export function productPointsHint(
  program: Pick<LoyaltyProgram, "basis" | "threshold" | "pointsValue">,
): string {
  return `Tiap ${thresholdLabel(program.basis, program.threshold)} → ${formatPoints(
    program.pointsValue,
  )}`;
}

/**
 * Kartu saldo beranda menampilkan bagian "Poin" (permintaan pemilik
 * 2026-09-30) bila toko sedang menjalankan program ATAU pembeli punya poin —
 * termasuk yang pernah punya (lifetime), karena poinnya mungkin baru saja
 * ditukar. Selain itu kartu saldo tetap seperti semula: tenant yang tak
 * memakai fitur poin tidak disodori "Poin 0".
 */
export function saldoCardShowsPoints(input: {
  hasRunningProgram: boolean;
  points: number;
  lifetimePoints: number;
}): boolean {
  return input.hasRunningProgram || input.points > 0 || input.lifetimePoints > 0;
}

/**
 * Isi strip "Program Poin Aktif" di beranda: NAMA program (permintaan pemilik
 * 2026-09-30, mis. "Reward Sarimbit 2027"); lebih dari satu → nama pertama +
 * "+N program lain". Aturan poinnya ada di halaman Poin Saya. null = tak ada.
 */
export function activeProgramsTitle(programs: Pick<LoyaltyProgram, "name">[]): string | null {
  if (programs.length === 0) return null;
  const first = programs[0].name.trim() || "Program poin";
  return programs.length > 1 ? `${first} +${programs.length - 1} program lain` : first;
}

// ── Tier (status dari lifetime_points, display-only) ─────────────────────────

export type LoyaltyTier = {
  name: string;
  minLifetime: number; // ambang lifetime_points
  color: string; // hex utk badge
};

export const DEFAULT_TIERS: LoyaltyTier[] = [
  { name: "Bronze", minLifetime: 0, color: "#CD7F32" },
  { name: "Silver", minLifetime: 500, color: "#9CA3AF" },
  { name: "Gold", minLifetime: 2000, color: "#F59E0B" },
  { name: "Platinum", minLifetime: 5000, color: "#6366F1" },
];

export type TierProgress = {
  /**
   * null = lifetime belum mencapai ambang tier TERENDAH (tier kustom tanpa
   * ambang 0). Dulu ia jatuh ke tier terendah — pembeli 0 poin tampil "Silver".
   */
  current: LoyaltyTier | null;
  next: LoyaltyTier | null;
  remaining: number; // poin lagi ke tier berikutnya (0 bila tertinggi)
};

/** Bersihkan + urutkan tier ascending by ambang. */
export function sortTiers(tiers: LoyaltyTier[]): LoyaltyTier[] {
  return tiers
    .filter((t) => t && typeof t.name === "string" && t.name.trim() !== "")
    .map((t) => ({
      name: t.name.trim(),
      minLifetime: Math.max(0, Math.trunc(Number(t.minLifetime) || 0)),
      color: typeof t.color === "string" && t.color ? t.color : "#9CA3AF",
    }))
    .sort((a, b) => a.minLifetime - b.minLifetime);
}

/**
 * JSON `site_settings.loyalty_tiers` → tier.
 *
 * - baris tak ada / bukan JSON / bukan array → `DEFAULT_TIERS` (toko belum
 *   pernah mengatur tier);
 * - array — TERMASUK `[]` — → daftar itu apa adanya. `[]` = admin MEMATIKAN
 *   tier (menghapus semua lalu Simpan Tier). Dulu `[]` dibaca "pakai bawaan":
 *   Galea menyimpan `[]` (2026-09-30 15:48 WIB) tapi pembeli tetap melihat
 *   Bronze/Silver/Gold/Platinum dan editor admin memunculkannya lagi.
 */
export function mapTiers(raw: unknown): LoyaltyTier[] {
  let arr: unknown = raw;
  if (typeof raw === "string") {
    try {
      arr = JSON.parse(raw);
    } catch {
      arr = null;
    }
  }
  if (!Array.isArray(arr)) return DEFAULT_TIERS;
  return sortTiers(
    arr.map((x) => {
      const o = (x ?? {}) as Record<string, unknown>;
      return {
        name: typeof o.name === "string" ? o.name : "",
        minLifetime: Number(o.minLifetime ?? 0),
        color: typeof o.color === "string" ? o.color : "#9CA3AF",
      };
    }),
  );
}

/**
 * Tier saat ini + tier berikutnya + sisa poin dari `lifetime_points`.
 * null = toko tidak memakai tier (daftar kosong) → badge tier disembunyikan.
 */
export function computeTier(
  lifetimePoints: number,
  tiers: LoyaltyTier[] = DEFAULT_TIERS,
): TierProgress | null {
  const list = sortTiers(tiers);
  if (list.length === 0) return null;
  const lp = Math.max(0, Number(lifetimePoints) || 0);
  let idx = -1;
  for (let i = 0; i < list.length; i++) {
    if (lp >= list[i].minLifetime) idx = i;
    else break;
  }
  const next = idx + 1 < list.length ? list[idx + 1] : null;
  return {
    current: idx >= 0 ? list[idx] : null,
    next,
    remaining: next ? Math.max(0, next.minLifetime - lp) : 0,
  };
}

// ── Validasi ─────────────────────────────────────────────────────────────────

/** Validasi draft program. Return pesan error atau null bila valid. */
export function validateProgramDraft(draft: LoyaltyProgramDraft): string | null {
  if (!draft.name || !draft.name.trim()) return "Nama program wajib diisi.";
  if (!Number.isFinite(draft.threshold) || draft.threshold <= 0) {
    return "Ambang (minimal pcs/nominal) harus lebih dari 0.";
  }
  // Threshold pecahan membuat TS & SQL berbeda hasil (F3-7) dan tak punya arti
  // untuk pcs maupun rupiah — ditolak di sini, bukan dibulatkan diam-diam.
  if (!Number.isInteger(draft.threshold)) {
    return "Ambang harus bilangan bulat (pcs atau rupiah utuh).";
  }
  if (!Number.isFinite(draft.pointsValue) || draft.pointsValue <= 0) {
    return "Nilai poin harus lebih dari 0.";
  }
  if (!Number.isInteger(draft.pointsValue)) {
    return "Nilai poin harus bilangan bulat.";
  }
  if (draft.startsAt && draft.endsAt) {
    const s = new Date(draft.startsAt).getTime();
    const e = new Date(draft.endsAt).getTime();
    if (Number.isFinite(s) && Number.isFinite(e) && s > e) {
      return "Tanggal mulai tidak boleh setelah tanggal berakhir.";
    }
  }
  if (draft.targetType !== "all" && draft.targets.length === 0) {
    return "Pilih minimal satu produk/kategori/merek untuk program ini.";
  }
  return null;
}

/** Validasi draft hadiah. */
export function validateRewardDraft(draft: LoyaltyRewardDraft): string | null {
  if (!draft.name || !draft.name.trim()) return "Nama hadiah wajib diisi.";
  if (!Number.isFinite(draft.pointsCost) || draft.pointsCost <= 0) {
    return "Biaya poin harus lebih dari 0.";
  }
  if (draft.stock !== null && (!Number.isFinite(draft.stock) || draft.stock < 0)) {
    return "Stok tidak boleh negatif.";
  }
  return null;
}

/** Validasi penukaran poin → saldo (cermin guard RPC redeem_points_to_saldo). */
export function validateRedeemSaldo(
  points: number,
  settings: LoyaltySettings,
  availablePoints: number,
): string | null {
  if (!settings.saldoEnabled) return "Penukaran poin ke saldo sedang tidak aktif.";
  // F4-4: cermin persis guard RPC — kurs null/<=0 ditolak, tanpa jatuh ke default.
  if (settings.rupiahPerPoint === null || settings.rupiahPerPoint <= 0) {
    return "Kurs penukaran belum diatur.";
  }
  if (!Number.isFinite(points) || points <= 0) return "Jumlah poin tidak valid.";
  if (points < settings.minRedeemPoints) {
    return `Minimal penukaran ${formatPoints(settings.minRedeemPoints)}.`;
  }
  if (points > availablePoints) return "Poin kamu tidak mencukupi.";
  if (pointsToRupiah(points, settings.rupiahPerPoint) <= 0) {
    return "Nominal saldo hasil tukar harus lebih dari 0.";
  }
  return null;
}

/**
 * Pesan galat hapus program untuk admin. Program yang SUDAH memberi poin tak
 * bisa dihapus: FK `loyalty_transactions.program_id ON DELETE SET NULL` harus
 * menulis ulang riwayatnya, dan trigger insert-only
 * (`prevent_loyalty_txn_mutation`) menolak — penolakan yang BENAR (riwayat poin
 * harus utuh), tapi pesannya mentah: "loyalty_transactions bersifat insert-only
 * (tidak boleh UPDATE )". Pesan lain diteruskan apa adanya.
 */
export function describeProgramDeleteError(message: string): string {
  return /insert-only/i.test(message)
    ? "Program ini sudah memberi poin ke pelanggan, jadi tidak bisa dihapus — riwayat poin " +
        "harus tetap utuh. Nonaktifkan saja lewat tombol ⏻."
    : message;
}

export function emptyProgramDraft(): LoyaltyProgramDraft {
  return {
    id: null,
    name: "",
    description: "",
    isActive: true,
    startsAt: null,
    endsAt: null,
    productTypeScope: "all",
    basis: "pcs",
    targetType: "all",
    threshold: 1,
    pointsValue: 1,
    targets: [],
    applyRetro: false,
  };
}

export function draftFromProgram(p: LoyaltyProgram): LoyaltyProgramDraft {
  return {
    id: p.id,
    name: p.name,
    description: p.description ?? "",
    isActive: p.isActive,
    startsAt: p.startsAt,
    endsAt: p.endsAt,
    productTypeScope: p.productTypeScope,
    basis: p.basis,
    targetType: p.targetType,
    threshold: p.threshold,
    pointsValue: p.pointsValue,
    targets: p.targets.map((t) => ({ ...t })),
    applyRetro: false,
  };
}

export function emptyRewardDraft(): LoyaltyRewardDraft {
  return {
    id: null,
    name: "",
    description: "",
    pointsCost: 1,
    stock: null,
    originalStock: null,
    imageUrl: null,
    isActive: true,
  };
}

export function draftFromReward(r: LoyaltyReward): LoyaltyRewardDraft {
  return {
    id: r.id,
    name: r.name,
    description: r.description ?? "",
    pointsCost: r.pointsCost,
    stock: r.stock,
    originalStock: r.stock,
    imageUrl: r.imageUrl,
    isActive: r.isActive,
  };
}

// ── Payload hadiah (bughunt F4-2) ────────────────────────────────────────────
//
// `loyalty_rewards.stock` adalah besaran HIDUP: `redeem_points_reward` men-
// dekremennya dan `admin_reject_redemption` merestoknya, keduanya lewat RPC.
// Form admin karena itu TIDAK BOLEH menuliskannya kembali pada penyimpanan
// biasa — angka yang dimuat saat modal dibuka sudah basi begitu ada satu klaim
// masuk, dan menuliskannya balik menghapus dekremen klaim itu (hadiah jadi bisa
// diklaim melebihi stok). Aturan yang sama sudah lama berlaku utk `products.stock`.

/** Payload EDIT hadiah — `stock` sengaja TIDAK ikut. */
export function rewardEditPayload(draft: LoyaltyRewardDraft): {
  name: string;
  description: string | null;
  points_cost: number;
  image_url: string | null;
  is_active: boolean;
} {
  return {
    name: draft.name.trim(),
    description: draft.description.trim() || null,
    points_cost: draft.pointsCost,
    image_url: draft.imageUrl,
    is_active: draft.isActive,
  };
}

/** Payload INSERT hadiah baru — di sini `stock` memang nilai awalnya. */
export function rewardInsertPayload(
  draft: LoyaltyRewardDraft,
): ReturnType<typeof rewardEditPayload> & { stock: number | null } {
  return { ...rewardEditPayload(draft), stock: draft.stock };
}

/** Benar hanya bila admin BENAR-BENAR menggeser stok di formulir. */
export function rewardStockChanged(draft: LoyaltyRewardDraft): boolean {
  return draft.stock !== draft.originalStock;
}

// ── Mapper baris DB (koersi defensif) ────────────────────────────────────────

function num(v: unknown, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function scopeOf(v: unknown): ProductTypeScope {
  return v === "preorder" || v === "ready_stock" ? v : "all";
}
function basisOf(v: unknown): ProgramBasis {
  return v === "nominal" ? "nominal" : "pcs";
}
function targetTypeOf(v: unknown): TargetType {
  return v === "product" || v === "category" || v === "brand" ? v : "all";
}
function targetKindOf(v: unknown): TargetKind {
  return v === "category" || v === "brand" ? v : "product";
}

export function mapTargetRow(row: Record<string, unknown>): LoyaltyTarget {
  return {
    kind: targetKindOf(row.kind),
    productId: (row.product_id as string) ?? null,
    name: typeof row.name === "string" ? row.name : "",
  };
}

export function mapProgramRow(
  row: Record<string, unknown>,
  targets: LoyaltyTarget[] = [],
): LoyaltyProgram {
  return {
    id: String(row.id ?? ""),
    name: typeof row.name === "string" ? row.name : "",
    description: (row.description as string) ?? null,
    isActive: !!row.is_active,
    startsAt: (row.starts_at as string) ?? null,
    endsAt: (row.ends_at as string) ?? null,
    productTypeScope: scopeOf(row.product_type_scope),
    basis: basisOf(row.basis),
    targetType: targetTypeOf(row.target_type),
    threshold: num(row.threshold, 0),
    pointsValue: Math.trunc(num(row.points_value, 0)),
    targets,
    createdAt: (row.created_at as string) ?? null,
  };
}

export function mapRewardRow(row: Record<string, unknown>): LoyaltyReward {
  const stockRaw = row.stock;
  return {
    id: String(row.id ?? ""),
    name: typeof row.name === "string" ? row.name : "",
    description: (row.description as string) ?? null,
    pointsCost: Math.trunc(num(row.points_cost, 0)),
    stock: stockRaw === null || stockRaw === undefined ? null : Math.trunc(num(stockRaw, 0)),
    imageUrl: (row.image_url as string) ?? null,
    isActive: !!row.is_active,
    createdAt: (row.created_at as string) ?? null,
  };
}

export function mapLoyaltyTxnRow(row: Record<string, unknown>): LoyaltyTxn {
  const type = String(row.type ?? "earn") as LoyaltyTxnType;
  return {
    id: String(row.id ?? ""),
    type: (TXN_TYPE_LABEL[type] ? type : "earn") as LoyaltyTxnType,
    direction: row.direction === "out" ? "out" : "in",
    points: Math.trunc(num(row.points, 0)),
    balanceAfter: Math.trunc(num(row.balance_after, 0)),
    programId: (row.program_id as string) ?? null,
    referenceId: (row.reference_id as string) ?? null,
    note: (row.note as string) ?? null,
    createdAt: (row.created_at as string) ?? null,
  };
}

/** Awalan catatan earn buatan award (mig 5160) & terapkan mundur (mig 6810). */
export const PROGRAM_EARN_NOTE_PREFIX = "Poin dari program: ";

/**
 * Catatan satu baris riwayat poin untuk DITAMPILKAN. Ledger immutable
 * menyimpan nama program SAAT poin diberikan ("Poin dari program: Reward Syawal
 * 2027"); program yang kemudian berganti nama tampil dengan nama sekarang
 * (bughunt F7-8) — catatan di ledger tetap utuh sebagai jejak. Program yang
 * tak terbaca (nonaktif / terhapus) → catatan apa adanya.
 */
export function txnDisplayNote(
  tx: Pick<LoyaltyTxn, "type" | "programId" | "note">,
  programNames: ReadonlyMap<string, string>,
): string | null {
  const note = tx.note;
  if (tx.type === "earn" && tx.programId && note?.startsWith(PROGRAM_EARN_NOTE_PREFIX)) {
    const current = programNames.get(tx.programId)?.trim();
    if (current) return PROGRAM_EARN_NOTE_PREFIX + current;
  }
  return note;
}

/**
 * Id nota yang dirujuk satu baris riwayat poin, atau null. Hanya `earn` (award
 * saat nota lunas) dan `revoke` (clawback saat nota dibatalkan) yang
 * `reference_id`-nya id NOTA; penukaran merujuk id klaim, penyesuaian admin
 * dan kedaluwarsa tak merujuk apa pun.
 */
export function txnInvoiceRef(tx: Pick<LoyaltyTxn, "type" | "referenceId">): string | null {
  if (tx.type !== "earn" && tx.type !== "revoke") return null;
  return tx.referenceId && tx.referenceId.trim() !== "" ? tx.referenceId : null;
}

/** Id nota unik yang dirujuk riwayat poin (urutan kemunculan). */
export function txnInvoiceRefs(txns: Pick<LoyaltyTxn, "type" | "referenceId">[]): string[] {
  const out: string[] = [];
  for (const tx of txns) {
    const ref = txnInvoiceRef(tx);
    if (ref && !out.includes(ref)) out.push(ref);
  }
  return out;
}

export function mapRedemptionRow(row: Record<string, unknown>): LoyaltyRedemption {
  const status = String(row.status ?? "pending") as RedemptionStatus;
  return {
    id: String(row.id ?? ""),
    customerId: String(row.customer_id ?? ""),
    type: row.type === "reward" ? "reward" : "saldo",
    rewardId: (row.reward_id as string) ?? null,
    rewardName: (row.reward_name as string) ?? null,
    pointsSpent: Math.trunc(num(row.points_spent, 0)),
    rupiahCredited:
      row.rupiah_credited === null || row.rupiah_credited === undefined
        ? null
        : Math.trunc(num(row.rupiah_credited, 0)),
    status: REDEMPTION_STATUS_META[status] ? status : "pending",
    note: (row.note as string) ?? null,
    createdAt: (row.created_at as string) ?? null,
    fulfilledAt: (row.fulfilled_at as string) ?? null,
  };
}

/**
 * Jejak penarikan poin yang TIDAK tertutup saldo poin (bughunt F4-1).
 * Ditulis dari dalam `clawback_loyalty_points_for_invoice` (mig 5270 → 5310).
 * `pointsUnrecoverable > 0` = masih perlu koreksi manual admin.
 */
export type LoyaltyClawbackShortfall = {
  id: string;
  invoiceId: string | null;
  customerId: string | null;
  pointsDue: number;
  pointsTaken: number;
  pointsShort: number;
  pointsViaSaldo: number;
  rupiahReclaimed: number;
  pointsUnrecoverable: number;
  createdAt: string | null;
};

export function mapClawbackShortfallRow(
  row: Record<string, unknown>,
): LoyaltyClawbackShortfall {
  return {
    id: String(row.id ?? ""),
    invoiceId: (row.invoice_id as string) ?? null,
    customerId: (row.customer_id as string) ?? null,
    pointsDue: Math.trunc(num(row.points_due, 0)),
    pointsTaken: Math.trunc(num(row.points_taken, 0)),
    pointsShort: Math.trunc(num(row.points_short, 0)),
    pointsViaSaldo: Math.trunc(num(row.points_via_saldo, 0)),
    rupiahReclaimed: Math.trunc(num(row.rupiah_reclaimed, 0)),
    pointsUnrecoverable: Math.trunc(num(row.points_unrecoverable, 0)),
    createdAt: (row.created_at as string) ?? null,
  };
}

/** Map baris site_settings (key-value) → LoyaltySettings. */
export function mapLoyaltySettings(rows: { key: string; value: string | null }[]): LoyaltySettings {
  const map = new Map(rows.map((r) => [r.key, r.value]));
  // F4-4: kurs TIDAK punya default. Baris hilang / kosong / bukan angka / <= 0
  // semuanya berarti "belum diatur" (null) — bukan Rp 100. Menjatuhkannya ke
  // sebuah angka membuat storefront menjanjikan rupiah yang RPC-nya tolak.
  const rupiahRaw = map.get("loyalty_rupiah_per_point");
  const rupiahNum = Number(rupiahRaw);
  const rupiah =
    rupiahRaw !== null && rupiahRaw !== undefined && rupiahRaw !== "" &&
    Number.isFinite(rupiahNum) && rupiahNum > 0
      ? rupiahNum
      : null;
  const min = num(map.get("loyalty_min_redeem_points"), DEFAULT_LOYALTY_SETTINGS.minRedeemPoints);
  const days = num(map.get("loyalty_points_expiry_days"), DEFAULT_LOYALTY_SETTINGS.expiryDays);
  return {
    saldoEnabled: map.get("loyalty_saldo_enabled") === "true",
    rupiahPerPoint: rupiah,
    minRedeemPoints: min >= 0 ? Math.trunc(min) : 0,
    expiryEnabled: map.get("loyalty_points_expiry_enabled") === "true",
    expiryDays: days > 0 ? Math.trunc(days) : DEFAULT_LOYALTY_SETTINGS.expiryDays,
  };
}

/** Payload program → JSON utk RPC admin_upsert_loyalty_program. */
export function programDraftToPayload(draft: LoyaltyProgramDraft): {
  program: Record<string, unknown>;
  targets: Record<string, unknown>[];
} {
  return {
    program: {
      id: draft.id ?? undefined,
      name: draft.name.trim(),
      description: draft.description.trim() || null,
      isActive: draft.isActive,
      startsAt: draft.startsAt || null,
      endsAt: draft.endsAt || null,
      productTypeScope: draft.productTypeScope,
      basis: draft.basis,
      targetType: draft.targetType,
      threshold: draft.threshold,
      pointsValue: draft.pointsValue,
      applyRetro: draft.applyRetro,
    },
    targets:
      draft.targetType === "all"
        ? []
        : draft.targets.map((t) => ({
            kind: draft.targetType,
            productId: t.productId ?? null,
            name: t.name,
          })),
  };
}

// ── Simpan program: kunci aturan & terapkan mundur (bughunt F7-2, mig 6810) ──

/**
 * JSON `admin_upsert_loyalty_program` → hasil. Toleran terhadap bentuk lama
 * (uuid polos, sebelum mig 6810) supaya layar tak pecah di jendela deploy.
 */
export function mapProgramSaveResult(raw: unknown): LoyaltyProgramSaveResult {
  if (typeof raw === "string") {
    return { id: raw || null, rulesChanged: false, dryRun: false, retro: null };
  }
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const r = (o.retro && typeof o.retro === "object" ? o.retro : null) as Record<string, unknown> | null;
  const n = (v: unknown) => {
    const x = Math.trunc(Number(v));
    return Number.isFinite(x) && x > 0 ? x : 0;
  };
  return {
    id: typeof o.id === "string" && o.id ? o.id : null,
    rulesChanged: o.rulesChanged === true,
    dryRun: o.dryRun === true,
    retro: r ? { invoices: n(r.invoices), customers: n(r.customers), points: n(r.points) } : null,
  };
}

/**
 * Checkbox "Terapkan juga ke nota yang sudah lunas" relevan bila ada nota lama
 * yang bisa tersentuh: program yang sudah tersimpan, atau program baru yang
 * mulainya sudah lewat (isi mundur, preseden mig 6650).
 */
export function programRetroApplicable(
  draft: Pick<LoyaltyProgramDraft, "id" | "startsAt">,
  now: Date = new Date(),
): boolean {
  if (draft.id) return true;
  if (!draft.startsAt) return false;
  const s = new Date(draft.startsAt).getTime();
  return Number.isFinite(s) && s <= now.getTime();
}

/** Teks konfirmasi sesudah pratinjau terapkan mundur (sebelum simpan sungguhan). */
export function retroConfirmMessage(preview: LoyaltyProgramSaveResult): string {
  const r = preview.retro;
  if (!r || r.points <= 0) {
    return "Tidak ada tagihan lunas yang bertambah poinnya dengan aturan ini. Simpan program?";
  }
  return (
    `Terapkan mundur akan menambah ${formatPoints(r.points)} ke ${r.invoices} tagihan lunas ` +
    `(${r.customers} pelanggan). Pelanggan menerima notifikasi "Poin Bertambah"; poin yang ` +
    `sudah diberikan tidak pernah dikurangi. Lanjutkan simpan?`
  );
}

/** Pesan banner sesudah simpan; null bila tak ada yang perlu dilaporkan. */
export function programSaveMessage(result: LoyaltyProgramSaveResult): string | null {
  const r = result.retro;
  if (!r) return null;
  if (r.points <= 0) return "Program tersimpan. Tidak ada tagihan lunas yang bertambah poinnya.";
  return (
    `Program tersimpan. Terapkan mundur: ${formatPoints(r.points)} ditambahkan ke ` +
    `${r.invoices} tagihan (${r.customers} pelanggan).`
  );
}
